import L from "leaflet";
import { storageTilesSasReadService } from "../../../comun/services/storage/StorageTilesSasReadService";
import { serviceProvider } from "../../../comun/provider/ServiceProvider";

/** Tile transparente (1x1 gif) para tiles faltantes: evita el icono de "imagen rota". */
const TRANSPARENT_TILE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

/**
 * Toggle de conteo de animales (tiles pre-renderizados) para el mapa.
 * Construye y gestiona su propia capa de tiles a partir del template
 * `conteoPreRender` de la finca, anexándole el SAS de lectura. Arranca apagado:
 * el SAS se pide la primera vez que se enciende (y se cachea entre toggles).
 * Usar: const ctrl = new ConteoAnimalToggleControl(conteoPreRender);
 *       ctrl.init(container, map);
 */
export class ConteoAnimalControl {
  /** Template de tiles del conteo (`conteoPreRender` de la finca). */
  private _template: string | null;
  /** Mapa Leaflet (se setea en `init`). */
  private _map!: L.Map;
  /** Capa de tiles del conteo (o null si está apagado). */
  private _layer: L.TileLayer | null = null;
  /** Botón del control. */
  private _btn: HTMLButtonElement | null = null;
  /** Estado del toggle (encendido/apagado). */
  private _visible = false;
  /** Handler del click del botón (nombrado para poder removerlo). */
  private _onClick = (): void => void this._toggle();

  constructor() {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    this._template = finca.conteoPreRender;
  }

  /** Registra el botón en el contenedor. Estado inicial: apagado. */
  init(parent: HTMLElement, map: L.Map): void {
    if (!this._template) return;

    this._map = map;

    const btn = L.DomUtil.create(
      "button",
      "sx-btn sx-map-btn",
    ) as HTMLButtonElement;
    btn.innerHTML = "Conteo";
    btn.title = "Conteo animales";
    L.DomEvent.disableClickPropagation(btn);
    L.DomEvent.on(btn, "click", this._onClick);
    this._btn = btn;

    parent.appendChild(btn);
  }

  /** Remueve la capa de tiles y el listener del botón. */
  dispose(): void {
    this._removeLayer();
    if (this._btn) L.DomEvent.off(this._btn, "click", this._onClick);
    this._btn = null;
  }

  /** Alterna el conteo: lo enciende (agrega la capa) o lo apaga (la remueve). */
  private async _toggle(): Promise<void> {
    this._visible = !this._visible;
    if (this._visible) {
      await this._addLayer();
    } else {
      this._removeLayer();
    }
  }

  /** Pide el SAS, arma la capa de tiles (con zIndex alto) y la agrega al mapa;
   *  si falla el SAS, revierte el estado del botón. */
  private async _addLayer(): Promise<void> {
    try {
      const sas = await storageTilesSasReadService.getAsync();
      // El usuario pudo apagar el conteo mientras se resolvía el SAS.
      if (!this._visible) return;
      this._layer = L.tileLayer(`${this._template}?${sas.sasToken}`, {
        maxZoom: 20,
        minNativeZoom: 14, // tiles del conteo solo existen en zoom 14-18;
        maxNativeZoom: 18, // fuera de ese rango Leaflet reescala los del borde
        errorTileUrl: TRANSPARENT_TILE,
        // zIndex alto: el conteo siempre por encima de las otras capas de tiles
        // (Google, ortomosaico), sin importar el orden de encendido/apagado.
        zIndex: 650,
      }).addTo(this._map);
      this._btn?.classList.add("sx-map-btn--active");
    } catch {
      // Falló la obtención del SAS: revertir el estado del botón.
      this._visible = false;
      this._btn?.classList.remove("sx-map-btn--active");
    }
  }

  /** Remueve la capa de tiles del conteo y desmarca el botón. */
  private _removeLayer(): void {
    this._layer?.remove();
    this._layer = null;
    this._btn?.classList.remove("sx-map-btn--active");
  }
}
